import { z } from "zod";
import { JOIN_CODE_ALPHABET, JOIN_CODE_LENGTH } from "./codes.js";
import { FIELD_MAX_LENGTH } from "./textguard.js";

/**
 * The single source of validation for client and worker. Nothing may define
 * its own ad-hoc shape: if a field is not described here, it does not exist.
 *
 * These schemas check shape and length only. Content filtering (blocklist,
 * contact patterns) happens in textguard.ts and always runs on the server.
 */

/**
 * Long enough for a rolled compound plus a disambiguating number
 * (docs/PROFILE.md §5). Stays lenient about shape so the short handles that
 * existing accounts were created with keep working.
 */
export const HANDLE = z
  .string()
  .regex(/^[a-z0-9_-]{3,40}$/, "handle must be 3-40 chars of a-z 0-9 _ -");

export const ULID = z.string().regex(/^[0-9A-HJKMNP-TV-Z]{26}$/, "not a ULID");

export const DAY = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "expected YYYY-MM-DD");

/** Base64 of the 32-byte client-side PBKDF2 output. Never the PIN itself. */
export const DERIVED_KEY = z.string().regex(/^[A-Za-z0-9+/]{43}=$/, "malformed derived key");

export const LANG_A = z.literal("de");
export const LANG_B = z.enum(["en", "fr"]);
export const DIRECTION = z.enum(["ab", "ba"]);
export const VERDICT = z.enum(["correct", "almost", "wrong"]);

export const DISPLAY_NAME = z.string().trim().min(2).max(FIELD_MAX_LENGTH.display_name);
export const LIST_TITLE = z.string().trim().min(2).max(FIELD_MAX_LENGTH.list_title);
export const SOURCE_HINT = z.string().trim().max(FIELD_MAX_LENGTH.source_hint);
export const TERM_TEXT = z.string().trim().min(1).max(FIELD_MAX_LENGTH.term);
export const CLASS_NAME = z.string().trim().min(2).max(FIELD_MAX_LENGTH.class_name);
export const ONE_TIME_CODE = z.string().trim().min(8).max(40);

// ---------- auth ----------

/** 8-char class join code. Optional on register — solo practice stays available. */
export const JOIN_CODE = z
  .string()
  .regex(new RegExp(`^[${JOIN_CODE_ALPHABET}]{${JOIN_CODE_LENGTH}}$`), "invalid join code");

export const registerInput = z.object({
  display_name: DISPLAY_NAME,
  handle: HANDLE,
  dk: DERIVED_KEY,
  join_code: JOIN_CODE.optional(),
});
export type RegisterInput = z.infer<typeof registerInput>;

export const loginInput = z.object({
  handle: HANDLE,
  dk: DERIVED_KEY,
});
export type LoginInput = z.infer<typeof loginInput>;

export const recoverInput = z.object({
  handle: HANDLE,
  recovery_code: ONE_TIME_CODE,
  dk: DERIVED_KEY,
});
export type RecoverInput = z.infer<typeof recoverInput>;

export const resetPinInput = z.object({
  handle: HANDLE,
  reset_code: ONE_TIME_CODE,
  dk: DERIVED_KEY,
});
export type ResetPinInput = z.infer<typeof resetPinInput>;

export const LIST_VISIBILITY = z.enum(["private", "class"]);

export const createClassInput = z.object({
  name: CLASS_NAME,
  adult_invite: ONE_TIME_CODE.optional(),
});
export type CreateClassInput = z.infer<typeof createClassInput>;

/** Redeem a co-lead invite for one class (issue #41). */
export const leadClassInput = z.object({
  invite_code: ONE_TIME_CODE,
});

export const joinClassInput = z.object({
  join_code: JOIN_CODE,
});
export type JoinClassInput = z.infer<typeof joinClassInput>;

// ---------- lists ----------

export const listItemInput = z.object({
  a: TERM_TEXT,
  b: TERM_TEXT,
});
export type ListItemInput = z.infer<typeof listItemInput>;

export const MAX_ITEMS_PER_LIST = 200;

export const createListInput = z.object({
  title: LIST_TITLE,
  // nullish, like updateListInput: the editor sends null for an empty hint on
  // both paths, and the worker treats null and undefined the same way.
  source_hint: SOURCE_HINT.nullish(),
  lang_a: LANG_A.default("de"),
  lang_b: LANG_B,
  due_date: DAY.nullish(),
  visibility: LIST_VISIBILITY.optional(),
  items: z.array(listItemInput).min(1).max(MAX_ITEMS_PER_LIST),
});
export type CreateListInput = z.infer<typeof createListInput>;

export const updateListInput = z.object({
  title: LIST_TITLE.optional(),
  source_hint: SOURCE_HINT.nullish(),
  due_date: DAY.nullish(),
  visibility: LIST_VISIBILITY.optional(),
  items: z.array(listItemInput).min(1).max(MAX_ITEMS_PER_LIST).optional(),
});
export type UpdateListInput = z.infer<typeof updateListInput>;

export const listScopeQuery = z.enum(["mine", "class", "archived"]);

/** «Aktuell», «Archiv» or neither, per child (issue #99). */
export const LIST_MARK = z.enum(["current", "archived"]);
export type ListMark = z.infer<typeof LIST_MARK>;
export const listMarkInput = z.object({ mark: LIST_MARK.nullable() });
/** More than this many «Aktuell» means nothing is current any more. */
export const MAX_CURRENT_LISTS = 3;

// ---------- practice ----------

export const PRACTICE_ROUND_SIZE = 10;

/** Pairs on one matching board. Under five words the form is not offered. */
export const MATCH_PAIRS = 5;

/** «Wiederholen» takes at most this many lists in one round (issue #101). */
export const MAX_REVIEW_LISTS = 10;

export const practiceSessionInput = z
  .object({
    list_id: ULID.nullish(),
    /**
     * "due" is «heute dran» across every list the child can see (issue #49),
     * "review" is «Wiederholen» across the lists in `list_ids` (issue #101).
     */
    mode: z.enum(["exam", "longterm", "weak", "due", "review"]).default("longterm"),
    /** Required for "due": one language per round, so the tiles never mix. */
    lang: LANG_B.optional(),
    /**
     * Required for "review", and only there. One language, checked by the
     * worker. Counted before the worker drops duplicates: harmless.
     */
    list_ids: z.array(ULID).min(1).max(MAX_REVIEW_LISTS).optional(),
    size: z.number().int().min(1).max(30).default(PRACTICE_ROUND_SIZE),
  })
  .refine((input) => input.mode !== "due" || (input.lang !== undefined && !input.list_id), {
    message: "a due round takes a language and no list",
  })
  .refine((input) => (input.mode === "review") === (input.list_ids !== undefined) && (input.mode !== "review" || !input.list_id), {
    message: "a review round takes list_ids and no list_id, and only a review round takes list_ids",
  });
export type PracticeSessionInput = z.infer<typeof practiceSessionInput>;

/**
 * One answered card. The verdict is decided on the client by checkAnswer from
 * this same package — it has to be, because offline rounds are graded in the
 * browser and submitted later. The worker owns the scheduling, not the
 * grading; see docs/ARCHITECTURE.md §6 and the note in routes/practice.ts.
 */
export const CARD_FORM = z.enum(["choice", "type", "match"]);

export const practiceResult = z.object({
  term_id: ULID,
  direction: DIRECTION,
  verdict: VERDICT,
  attempt: z.number().int().min(1).max(5).default(1),
  /**
   * How the card was asked — it decides how far the answer may promote the
   * card. Defaults to typing so that a round queued by an older client keeps
   * scheduling exactly as it did before the forms existed.
   */
  form: CARD_FORM.default("type"),
});
export type PracticeResult = z.infer<typeof practiceResult>;

export const practiceResultsInput = z.object({
  list_id: ULID.nullish(),
  /** Wall-clock seconds the round took, clamped server-side. */
  seconds: z.number().int().min(0).max(3600).default(0),
  /**
   * Per word at most 3 attempts as cards (ROUND_REPEATS_MAX) and 2 on the
   * warm-up board (one wrong, then the hit; MatchBoard), so a round of 10 tops
   * out at 30. Older clients sent up to 6 per word on a board; the worker only
   * loops over these once, and 100 leaves room for both.
   */
  results: z.array(practiceResult).min(1).max(100),
  /**
   * False when the child left with «Abbrechen». The answers still count, the
   * day does not: a streak day needs a finished round (docs/UX.md §6).
   */
  complete: z.boolean().default(true),
  /**
   * Made on the device when the round ends, the same on every resend: a round
   * that arrived once is never counted twice (issue #118). Optional for
   * clients from before the offline queue.
   */
  round_id: ULID.optional(),
  /**
   * Unix seconds on the device clock when the round ended. A round sent late
   * counts for that day, at most LATE_ROUND_DAYS back (`playedWhen`).
   */
  played_at: z.number().int().min(0).optional(),
});
export type PracticeResultsInput = z.infer<typeof practiceResultsInput>;

// ---------- responses ----------

export interface ApiError {
  error: string;
  message: string;
}
